import { Component, inject, OnInit, ViewEncapsulation } from "@angular/core";
import { Router } from "@angular/router";
import {
  TableActionConfig,
  TableColumn,
  TableConfig,
  TableDateRangeEvent,
  TableFilterChangeEvent,
  TablePagination,
  TableSearchEvent,
  TableSort,
} from "../../../../Common/Components/Table/Type/TableType";
import { AppTableComponent } from "../../../../Common/Components/Table/TableComponent";
import { PrintingEffects } from "../../State/PrintingEffects";
import { PrintingState } from "../../State/PrintingState";
import { PrintingEntity } from "../../Dto/PrintingModel";
import { formatDateNumeric } from "../../../../Common/Utility/UtilityDate";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { ConfirmModalEffects } from "../../../../Common/Components/ConfirmModal/State/ConfirmModalEffects";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-printing-home",
  standalone: true,
  imports: [AppTableComponent],
  templateUrl: "./PrintingHomePage.html",
  styleUrl: "./PrintingHomePage.scss",
  encapsulation: ViewEncapsulation.None,
})
export class PrintingHomePage implements OnInit {
  private readonly printingEffects = inject(PrintingEffects);
  readonly printingState = inject(PrintingState);
  private readonly router = inject(Router);
  private readonly toastEffects = inject(ToastEffects);
  private readonly confirmModalEffects = inject(ConfirmModalEffects);
  selectedStatusFilter = "";
  readonly printingStatusFilterOptions: { value: PrintingEntity["status"]; label: string }[] = [
    { value: "Draft", label: "Draft" },
    { value: "Confirmed", label: "Confirmed" },
    { value: "Shipped", label: "Shipped" },
    { value: "Inspected", label: "Inspected" },
  ];

  columns: TableColumn<PrintingEntity>[] = [
    { key: "printingNo", header: "Printing No", sortable: true, width: "20%" },
    {
      key: "vendor.name",
      header: "Vendor",
      sortable: false,
      width: "20%",
      cellTemplate: (item) => item.vendor?.name || "-",
    },
    {
      key: "warehouse.name",
      header: "Warehouse",
      sortable: false,
      width: "20%",
      cellTemplate: (item) => item.warehouse?.name || "-",
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      width: "15%",
      cellTemplate: (item) => this.getStatusLabel(item.status),
      cellClass: (item) => this.getStatusChipClass(item.status),
      cellIcon: (item) => this.getStatusChipIcon(item.status),
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      width: "15%",
      cellTemplate: (item) =>
        (() => {
          if (item.createdAt) {
            return formatDateNumeric(item.createdAt);
          }
          return "-";
        })(),
    },
    {
      key: "updatedAt",
      header: "Updated",
      sortable: true,
      width: "10%",
      cellTemplate: (item) =>
        (() => {
          if (item.updatedAt) {
            return formatDateNumeric(item.updatedAt);
          }
          return "-";
        })(),
    },
  ];

  get config(): TableConfig<PrintingEntity> {
    return {
      loading: this.printingState.loading(),
      emptyMessage: this.printingState.error(),
      showActions: true,
      showSearch: true,
      searchPlaceholder: "Search printings...",
      showCreate: true,
      createButtonLabel: "Create Printing",
      createButtonIcon: "add",
      showDateRangeFilter: true,
      dateRangeFilterLabel: "Filter by creation date",
      filters: [
        {
          key: "status",
          label: "Status",
          placeholder: "All status",
          options: this.printingStatusFilterOptions,
          defaultValue: this.selectedStatusFilter,
        },
      ],
      onCreate: () => this.router.navigate(["/printings/create"]),
      actionButtons: [
        { label: "edit", icon: "edit", handler: (item) => this.router.navigate(["/printings/edit", item.id]) },
        {
          label: "confirm",
          icon: "check",
          visible: (item) => item.status === "Draft",
          handler: (item) => this.openConfirmPrintingModal(item),
        },
        {
          label: "ship",
          icon: "local_shipping",
          visible: (item) => item.status === "Confirmed",
          handler: (item) => this.openShipPrintingModal(item),
        },
        {
          label: "delete",
          icon: "delete",
          visible: (item) => item.status === "Draft",
          handler: (item) => this.openDeletePrintingModal(item),
        },
      ],
    };
  }

  get pagination(): TablePagination {
    return this.printingState.pagination();
  }

  ngOnInit(): void {
    const current = this.printingState.searchParams();
    this.selectedStatusFilter = current.status ?? "";
    this.printingEffects.searchPrintings(current);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSortChange(sort: TableSort | null): void {
    return;
  }

  onPageChange(event: { page: number; search?: string }): void {
    const current = this.printingState.searchParams();
    this.printingEffects.searchPrintings({ ...current, page: event.page, search: event.search });
  }

  onPageSizeChange(event: { limit: number; search?: string }): void {
    const current = this.printingState.searchParams();
    this.printingEffects.searchPrintings({ ...current, page: 1, limit: event.limit, search: event.search });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onActionClick<T>(event: { action: TableActionConfig<T>; item: T }): void {
    return;
  }

  onSearchChange(event: TableSearchEvent): void {
    const current = this.printingState.searchParams();
    this.printingEffects.searchPrintings({
      ...current,
      search: event.term,
      page: event.page,
    });
  }

  onFilterChange(event: TableFilterChangeEvent): void {
    this.selectedStatusFilter = event.filters["status"] ?? "";

    const current = this.printingState.searchParams();
    const nextStatus = this.selectedStatusFilter.trim() as PrintingEntity["status"] | "";
    if (!nextStatus) {
      this.printingEffects.searchPrintings({
        ...current,
        status: undefined,
        page: 1,
      });
      return;
    }

    this.printingEffects.searchPrintings({
      ...current,
      status: nextStatus,
      page: 1,
    });
  }

  onDateRangeChange(event: TableDateRangeEvent): void {
    const current = this.printingState.searchParams();
    this.printingEffects.searchPrintings({
      ...current,
      page: event.page,
      createdAtFrom: event.dateRange.from,
      createdAtTo: event.dateRange.to,
    });
  }

  async confirm(item: PrintingEntity): Promise<void> {
    try {
      await this.printingEffects.confirmPrinting(item.id);
      this.toastEffects.show("Printing confirmed.", "success");
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to confirm printing."), "error");
    }
  }

  openConfirmPrintingModal(item: PrintingEntity): void {
    this.confirmModalEffects.open({
      title: "Confirm Printing",
      message: `Are you sure you want to confirm printing "${item.printingNo}"?`,
      confirmText: "Confirm Printing",
      cancelText: "Cancel",
      variant: "primary",
      closeOnOverlay: true,
      onConfirm: async () => {
        await this.confirm(item);
      },
    });
  }

  openShipPrintingModal(item: PrintingEntity): void {
    this.confirmModalEffects.open({
      title: "Ship Printing",
      message: `Are you sure you want to mark printing "${item.printingNo}" as shipped?`,
      confirmText: "Ship Printing",
      cancelText: "Cancel",
      variant: "primary",
      closeOnOverlay: true,
      onConfirm: async () => {
        await this.ship(item);
      },
    });
  }

  openDeletePrintingModal(item: PrintingEntity): void {
    this.confirmModalEffects.open({
      title: "Delete Printing",
      message: `Are you sure you want to delete printing "${item.printingNo}"? This action cannot be undone.`,
      confirmText: "Delete Printing",
      cancelText: "Cancel",
      variant: "danger",
      closeOnOverlay: true,
      onConfirm: async () => {
        await this.delete(item);
      },
    });
  }

  async ship(item: PrintingEntity): Promise<void> {
    try {
      await this.printingEffects.shipPrinting(item.id);
      this.toastEffects.show("Printing shipped.", "success");
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to ship printing."), "error");
    }
  }

  async delete(item: PrintingEntity): Promise<void> {
    try {
      await this.printingEffects.deletePrinting(item.id);
      this.toastEffects.show("Printing deleted.", "success");
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to delete printing."), "error");
    }
  }

  private getStatusChipClass(status: PrintingEntity["status"]): string {
    if (status === "Draft") {
      return "app-table__chip app-table__chip--neutral";
    }
    if (status === "Confirmed") {
      return "app-table__chip app-table__chip--info";
    }
    if (status === "Shipped") {
      return "app-table__chip app-table__chip--warning";
    }
    return "app-table__chip app-table__chip--success";
  }

  private getStatusLabel(status: PrintingEntity["status"]): string {
    return this.printingStatusFilterOptions.find((option) => option.value === status)?.label ?? status;
  }

  private getStatusChipIcon(status: PrintingEntity["status"]): string {
    if (status === "Draft") {
      return "schedule";
    }
    if (status === "Confirmed") {
      return "check_circle";
    }
    if (status === "Shipped") {
      return "local_shipping";
    }
    return "verified";
  }
}
